import * as i0 from '@angular/core';
import { inject, ElementRef, input, output, Directive, HostListener } from '@angular/core';
import { fromEvent, tap, merge, takeUntil } from 'rxjs';
import { switchMap } from 'rxjs/operators';
let NgxSwipeDetectorDirective = /*#__PURE__*/(() => {
  class NgxSwipeDetectorDirective {
    constructor() {
      // Injections
      this.el = inject(ElementRef);
      // Inputs
      this.swipeThreshold = input(50);
      this.tapThreshold = 20;
      this.axisToLockOn = input('BOTH');
      // Outputs
      this.swipedLeft = output();
      this.swipedRight = output();
      this.swipedX = output();
      this.swipedY = output();
      this.swipedUp = output();
      this.swipedDown = output();
      this.isSwipingVertically = output();
      this.isSwipingHorizontally = output();
      this.swiping = output();
      this.swipeStart = output();
      this.swipeEnd = output();
      this.singleTap = output();
      // Variables
      this.isSwipeDirectionDetected = false;
      this.internalMovingVertically = false;
      this.startX = 0;
      this.startY = 0;
      this.isSwiping = false;
      this.subscriptions = [];
    }
    ngOnInit() {
      this.subscribeToPointerEvents();
    }
    subscribeToPointerEvents() {
      const hostElement = this.el.nativeElement;
      // Listen to pointerdown, pointerleave, and pointerup in one subscription
      const pointerEvents$ = fromEvent(hostElement, 'pointerdown').pipe(tap(event => this.onStart(event.clientX, event.clientY)), switchMap(() => merge(fromEvent(hostElement, 'pointermove').pipe(tap(event => {
        if (this.isSwiping && event.type !== 'touch') {
          this.onMove(event.clientX, event.clientY);
        }
      })), fromEvent(hostElement, 'pointerleave').pipe(tap(event => this.onEnd(event.clientX, event.clientY)))).pipe(takeUntil(fromEvent(hostElement, 'pointerup').pipe(tap(event => this.onEnd(event.clientX, event.clientY)))))));
      const pointerSubscription = pointerEvents$.subscribe();
      this.subscriptions.push(pointerSubscription);
    }
    onStart(clientX, clientY) {
      this.isSwiping = true;
      this.startX = clientX;
      this.startY = clientY;
      this.swipeStart.emit({
        x: this.startX,
        y: this.startY
      });
    }
    onTouchMove(event) {
      if (!this.isSwiping) {
        return;
      }
      const touch = event.changedTouches ? event.changedTouches[0] : event;
      this.onMove(touch.clientX, touch.clientY, event);
    }
    onMove(clientX, clientY, event) {
      const deltaX = clientX - this.startX;
      const deltaY = clientY - this.startY;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);
      // Detect swipe direction at the start
      if (!this.isSwipeDirectionDetected) {
        if (absDeltaY > absDeltaX) {
          this.isSwipingVertically.emit(true);
          this.internalMovingVertically = true;
          this.isSwipingHorizontally.emit(false);
          this.isSwipeDirectionDetected = true;
        } else if (absDeltaX > absDeltaY) {
          this.isSwipingVertically.emit(false);
          this.internalMovingVertically = false;
          this.isSwipingHorizontally.emit(true);
          this.isSwipeDirectionDetected = true;
        }
      }
      // Auto End the swipe
      if (this.swipeTimeOut) {
        clearTimeout(this.swipeTimeOut);
        this.swipeTimeOut = undefined;
      }
      this.swipeTimeOut = setTimeout(() => {
        this.onEnd(clientX, clientY);
      }, 500);
      this.swiping.emit({
        clientX,
        clientY,
        deltaX,
        deltaY
      });
      if (event && this.isSwipeDirectionDetected) {
        if (this.axisToLockOn() === 'X' && !this.internalMovingVertically) {
          event.preventDefault();
        } else if (this.axisToLockOn() === 'Y' && this.internalMovingVertically) {
          event.preventDefault();
        } else if (this.axisToLockOn() === 'BOTH') {
          event.preventDefault();
        }
      }
    }
    onEnd(clientX, clientY) {
      if (this.swipeTimeOut) {
        clearTimeout(this.swipeTimeOut);
        this.swipeTimeOut = undefined;
      }
      this.isSwiping = false;
      this.isSwipeDirectionDetected = false;
      this.swipeEnd.emit({
        x: clientX,
        y: clientY
      });
      this.isSwipingVertically.emit(false);
      this.isSwipingHorizontally.emit(false);
      const deltaX = clientX - this.startX;
      const deltaY = clientY - this.startY;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);
      if (absDeltaX < this.tapThreshold && absDeltaY < this.tapThreshold) {
        this.singleTap.emit();
      } else {
        if (absDeltaX > absDeltaY) {
          this.swipedX.emit(deltaX);
          if (deltaX > 0) {
            this.swipedRight.emit(deltaX);
          } else {
            this.swipedLeft.emit(deltaX);
          }
        } else if (absDeltaY > absDeltaX) {
          this.swipedY.emit(deltaY);
          if (deltaY > 0) {
            this.swipedDown.emit(deltaY);
          } else {
            this.swipedUp.emit(deltaY);
          }
        }
      }
    }
    ngOnDestroy() {
      this.subscriptions.forEach(sub => sub && sub.unsubscribe());
    }
    static {
      this.ɵfac = function NgxSwipeDetectorDirective_Factory(t) {
        return new (t || NgxSwipeDetectorDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgxSwipeDetectorDirective,
        selectors: [["", "ngxSwipeDetector", ""]],
        hostBindings: function NgxSwipeDetectorDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("touchmove", function NgxSwipeDetectorDirective_touchmove_HostBindingHandler($event) {
              return ctx.onTouchMove($event);
            });
          }
        },
        inputs: {
          swipeThreshold: [i0.ɵɵInputFlags.SignalBased, "swipeThreshold"],
          axisToLockOn: [i0.ɵɵInputFlags.SignalBased, "axisToLockOn"]
        },
        outputs: {
          swipedLeft: "swipedLeft",
          swipedRight: "swipedRight",
          swipedX: "swipedX",
          swipedY: "swipedY",
          swipedUp: "swipedUp",
          swipedDown: "swipedDown",
          isSwipingVertically: "isSwipingVertically",
          isSwipingHorizontally: "isSwipingHorizontally",
          swiping: "swiping",
          swipeStart: "swipeStart",
          swipeEnd: "swipeEnd",
          singleTap: "singleTap"
        },
        standalone: true
      });
    }
  }
  return NgxSwipeDetectorDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngx-swipe-detector
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgxSwipeDetectorDirective };
